import type {NotificationItem, NotificationType} from "../types";
import {isUrgentNotificationType} from "./notification-feed-topics";

export const DEFAULT_URGENT_VIBRATE_PATTERN = [200, 100, 200, 100, 200] as const;

const ATTENTION_DEBOUNCE_MS = 4_000;
const recentAttentionKeys = new Map<string, number>();

let sharedAudioContext: AudioContext | null = null;
let audioUnlockInstalled = false;

/** Resume AudioContext after first user gesture (required on most browsers). */
export function initNotificationAttentionAudio(): void {
  if (typeof window === "undefined" || audioUnlockInstalled) {
    return;
  }
  audioUnlockInstalled = true;

  const unlock = () => {
    const AudioContextCtor =
      window.AudioContext ??
      (window as typeof window & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }
    try {
      sharedAudioContext ??= new AudioContextCtor();
      void sharedAudioContext.resume();
    } catch {
      // Unsupported or blocked
    }
  };

  window.addEventListener("pointerdown", unlock, {once: true, passive: true});
  window.addEventListener("keydown", unlock, {once: true});
}

/** Short chime via Web Audio (no external asset — avoids 404 on missing mp3). */
function playSyntheticAlertSound(): void {
  if (typeof window === "undefined") {
    return;
  }
  const AudioContextCtor =
    window.AudioContext ??
    (window as typeof window & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
  if (!AudioContextCtor) {
    return;
  }
  try {
    const ctx = sharedAudioContext ?? new AudioContextCtor();
    const play = () => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 880;
      gain.gain.value = 0.08;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.18);
      oscillator.onended = () => {
        if (ctx !== sharedAudioContext) {
          void ctx.close();
        }
      };
    };

    if (ctx.state === "suspended") {
      void ctx.resume().then(play).catch(() => {});
      return;
    }
    play();
  } catch {
    // Unsupported or blocked
  }
}

export function vibrateUrgentPattern(): void {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    navigator.vibrate([...DEFAULT_URGENT_VIBRATE_PATTERN]);
  }
}

export function playNotificationAlertSound(): void {
  playSyntheticAlertSound();
}

function readPayloadNumber(payload: Record<string, unknown> | undefined, key: string): number | null {
  const value = payload?.[key];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Aligns with break-notice / shift-end gate attention keys so realtime + modal do not double-chime. */
export function getNotificationAttentionDedupeKey(item: NotificationItem): string {
  const payload = item.payload;
  if (item.type === "shift.break_alert") {
    const breakId = readPayloadNumber(payload, "shift_break_id") ?? item.subject.id;
    return `break-${breakId}`;
  }
  if (item.type === "shift.end_alert") {
    const shiftId = readPayloadNumber(payload, "shift_id") ?? item.subject.id;
    return `shift-end-${shiftId}`;
  }
  return item.id;
}

export function triggerNotificationAttention(type: NotificationType, dedupeKey?: string): void {
  if (!isUrgentNotificationType(type)) {
    return;
  }

  const key = dedupeKey ?? type;
  const now = Date.now();
  const last = recentAttentionKeys.get(key);
  if (last != null && now - last < ATTENTION_DEBOUNCE_MS) {
    return;
  }
  recentAttentionKeys.set(key, now);

  playNotificationAlertSound();
  vibrateUrgentPattern();
}
